import { AlertTriangle, Clock3, MapPin, ShieldCheck, UserRound, WifiOff, Wrench, Zap } from "lucide-react";
import { isInternalWorkOrderNo } from "../work-order-display";
import { statusLabel, impactLabel, teamStatusLabel, durationLabel, shortTime, type TechnicalBoardCall, type TechnicalBoardTeamMember } from "./technical-board-model";

export function CallTimeline({ item }: { item: TechnicalBoardCall }) {
  const steps = [
    { label: "Çağrı", value: item.createdAt, complete: true },
    { label: "Üstlenme", value: item.claimedAt, complete: Boolean(item.claimedAt) },
    { label: "Çözüm", value: item.resolvedAt, complete: Boolean(item.resolvedAt) },
    { label: "Onay", value: item.closedAt, complete: Boolean(item.closedAt) },
  ];
  return (
    <ol className="technical-board-timeline" aria-label="Teknik çağrı zaman çizgisi">
      {steps.map((step) => (
        <li className={step.complete ? "is-complete" : ""} key={step.label}>
          <i aria-hidden="true" />
          <span><small>{step.label}</small><strong>{shortTime(step.value)}</strong></span>
        </li>
      ))}
    </ol>
  );
}

export function CallCard({ item, now }: { item: TechnicalBoardCall; now: number }) {
  const DepartmentIcon = item.department === "ELECTRICAL" ? Zap : Wrench;
  const timingFrom = item.status === "IN_PROGRESS" && item.claimedAt ? item.claimedAt : item.createdAt;
  return (
    <article
      className={`technical-board-card is-${item.status.toLowerCase().replaceAll("_", "-")} is-impact-${item.impact.toLowerCase()}`}
      aria-label={`${item.lineName}, ${statusLabel[item.status]}, ${impactLabel[item.impact]}`}
    >
      <header>
        <span className="technical-board-department">
          <DepartmentIcon aria-hidden="true" />
          {item.department === "ELECTRICAL" ? "Elektrik" : "Mekanik"}
        </span>
        <span className="technical-board-elapsed">
          <Clock3 aria-hidden="true" />
          {durationLabel(timingFrom, now)}
        </span>
      </header>
      <div className="technical-board-card-title">
        <div>
          <small>{item.lineCode}</small>
          <h2>{item.lineName}</h2>
        </div>
        <span className="technical-board-status">{statusLabel[item.status]}</span>
      </div>
      <p className="technical-board-note">{item.note}</p>
      {item.status === "RECORDED" && item.notificationState !== "DELIVERED" ? (
        <p className="technical-board-notification-state"><WifiOff aria-hidden="true" />Harici bildirim kanalı bağlı değil</p>
      ) : null}
      {item.waitingReason ? (
        <p className="technical-board-waiting-reason">
          <AlertTriangle aria-hidden="true" />
          <span><small>BEKLEME NEDENİ</small>{item.waitingReason}</span>
        </p>
      ) : null}
      <CallTimeline item={item} />
      <dl>
        <div>
          <dt>Üretim etkisi</dt>
          <dd>{impactLabel[item.impact]}</dd>
        </div>
        <div>
          <dt>{item.assignedTechnicianName ? "Teknik personel" : "Bildiren"}</dt>
          <dd>
            <UserRound aria-hidden="true" />
            {item.assignedTechnicianName ?? item.operatorName}
          </dd>
        </div>
      </dl>
      <footer>
        <span>{item.workOrderNo && !isInternalWorkOrderNo(item.workOrderNo) ? item.workOrderNo : "Plandan üretim"}</span>
        <span>{item.productName ?? "Ürün bilgisi yok"}</span>
      </footer>
    </article>
  );
}

export function CriticalCall({ item, now }: { item: TechnicalBoardCall; now: number }) {
  const DepartmentIcon = item.department === "ELECTRICAL" ? Zap : Wrench;
  return (
    <section className="technical-board-critical" aria-label={`Öncelikli çağrı: ${item.lineName}`}>
      <div className="technical-board-critical-lead">
        <span><AlertTriangle aria-hidden="true" /> ÖNCELİKLİ MÜDAHALE</span>
        <strong>{durationLabel(item.createdAt, now)}</strong>
      </div>
      <div className="technical-board-critical-main">
        <div className="technical-board-critical-machine">
          <small>{item.lineCode}</small>
          <h2>{item.lineName}</h2>
          <span><DepartmentIcon aria-hidden="true" />{item.department === "ELECTRICAL" ? "Elektrik" : "Mekanik"}</span>
        </div>
        <div className="technical-board-critical-problem">
          <small>SORUN</small>
          <p>{item.note}</p>
          <span>{impactLabel[item.impact]} · Bildiren: {item.operatorName}</span>
        </div>
        <div className="technical-board-critical-owner">
          <small>SORUMLU</small>
          <strong>{item.assignedTechnicianName ?? "Henüz üstlenilmedi"}</strong>
          <span>{statusLabel[item.status]}</span>
          {item.status === "RECORDED" && item.notificationState !== "DELIVERED" ? <small className="is-warning">HARİCİ BİLDİRİM YOK</small> : null}
        </div>
      </div>
      <CallTimeline item={item} />
    </section>
  );
}

export function TeamPanel({ members }: { members: TechnicalBoardTeamMember[] }) {
  return (
    <aside className="technical-board-team" aria-label="Vardiyadaki teknik ekip">
      <header>
        <span><ShieldCheck aria-hidden="true" />Vardiyadaki ekip</span>
        <strong>{members.filter((member) => member.status !== "OFF_DUTY").length}</strong>
      </header>
      <div className="technical-board-team-list">
        {members.length ? members.map((member) => (
          <article key={member.id}>
            <i className={`is-${member.status.toLowerCase().replaceAll("_", "-")}`} aria-hidden="true" />
            <div>
              <strong>{member.name}</strong>
              <small>{member.department === "ELECTRICAL" ? "Elektrik" : "Mekanik"}</small>
            </div>
            <span>
              <strong>{teamStatusLabel[member.status]}</strong>
              {member.currentLineName ? <small><MapPin aria-hidden="true" />{member.currentLineName}</small> : null}
            </span>
          </article>
        )) : (
          <div className="technical-board-team-empty">
            <AlertTriangle aria-hidden="true" />
            <strong>Vardiya ekibi atanmadı</strong>
          </div>
        )}
      </div>
    </aside>
  );
}
